import type { CartItem } from '../types';

/**
 * Lieux compatibles avec le panier = intersection des `pro_place_ids` de chaque item et de chaque
 * supplément retenu (la réservation se déroule à un seul lieu ; un supplément lié à une salle n'est
 * proposé que là où elle se trouve). Convention : une liste vide/absente n'impose aucune contrainte.
 * Aucun item contraignant ⇒ tous les lieux.
 */
export function computeCompatiblePlaceIds(cartItems: CartItem[], allPlaceIds: number[]): Set<number> {
  let allowed: Set<number> | null = null;
  const constraints = cartItems.flatMap(
    (item): Array<number[] | undefined> => [
      item.prestation.pro_place_ids,
      ...(item.supplements ?? [])
        .filter((supplement): boolean => supplement.selected)
        .map((supplement): number[] | undefined => supplement.pro_place_ids),
    ],
  );
  for (const ids of constraints) {
    if (!ids || ids.length === 0) continue;
    const itemSet = new Set(ids);
    allowed = allowed === null ? itemSet : new Set([...allowed].filter((id): boolean => itemSet.has(id)));
  }
  return allowed ?? new Set(allPlaceIds);
}

/**
 * Une prestation candidate est compatible « lieu » avec le panier s'il reste au moins un lieu
 * proposant TOUTES les prestations du panier ET cette prestation. `pro_place_ids` vide/absent
 * sur la candidate = disponible partout. Statique (aucun appel réseau) : sert à griser à l'étape
 * « prestations » les prestations qui n'ont aucun lieu commun avec la sélection courante.
 */
export function isPrestationPlaceCompatible(
  prestationPlaceIds: number[] | undefined,
  cartItems: CartItem[],
  allPlaceIds: number[],
): boolean {
  const cartAllowed = computeCompatiblePlaceIds(cartItems, allPlaceIds);
  if (!prestationPlaceIds || prestationPlaceIds.length === 0) return cartAllowed.size > 0;
  return prestationPlaceIds.some((id): boolean => cartAllowed.has(id));
}

/**
 * Un membre est compatible avec le lieu sélectionné si ses `pro_place_ids` (lieux dérivés des
 * availabilities) sont vides/absents (« tous lieux », aucune contrainte) ou contiennent le lieu.
 * Sert à griser à l'étape « membre » les membres qui n'interviennent pas au lieu choisi, comme on
 * grise déjà les lieux/prestations incompatibles. Pas de lieu sélectionné ⇒ aucune contrainte.
 */
export function isMemberPlaceCompatible(
  memberPlaceIds: number[] | undefined,
  selectedPlaceId: number | undefined,
): boolean {
  if (selectedPlaceId === undefined) return true;
  if (!memberPlaceIds || memberPlaceIds.length === 0) return true;
  return memberPlaceIds.includes(selectedPlaceId);
}
